import { useState } from "react";
import { Upload, X, FileImage, FileVideo } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent } from "./ui/card";
import { useToast } from "../hooks/use-toast";
import { quoteFormData } from "../mockData";

export const QuoteForm = () => {
  const { toast } = useToast();
  const [files, setFiles] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    urgency: "",
    message: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleFileUpload = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const submissions = JSON.parse(localStorage.getItem('quoteSubmissions') || '[]');
    submissions.push({
      ...formData,
      files: files.map(f => f.name),
      id: Date.now(),
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('quoteSubmissions', JSON.stringify(submissions));

    toast({
      title: "Quote Request Submitted!",
      description: "We'll get back to you within 24 hours.",
    });

    setFormData({
      name: "",
      email: "",
      phone: "",
      service: "",
      urgency: "",
      message: ""
    });
    setFiles([]);
  };

  return (
    <Card className="border-0 shadow-2xl bg-white/5 backdrop-blur-md border border-white/10">
      <CardContent className="p-8 md:p-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Request a Free Quote
          </h2>
          <p className="text-gray-300">
            Tell us about your project and upload photos or videos
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="name" className="text-white">Full Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                placeholder="John Doe"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-white">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="phone" className="text-white">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                placeholder="07298 873176"
              />
            </div>

            <div>
              <Label htmlFor="service" className="text-white">Service Needed *</Label>
              <Select 
                value={formData.service} 
                onValueChange={(value) => handleSelectChange("service", value)}
                required
              >
                <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {quoteFormData.services.map((service) => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="urgency" className="text-white">Project Timeline *</Label>
            <Select 
              value={formData.urgency} 
              onValueChange={(value) => handleSelectChange("urgency", value)}
              required
            >
              <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="When do you need this done?" />
              </SelectTrigger>
              <SelectContent>
                {quoteFormData.urgency.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="message" className="text-white">Project Description *</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              className="mt-1 min-h-32 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              placeholder="Please describe your project in detail..."
            />
          </div>

          {/* File Upload */}
          <div>
            <Label className="text-white mb-2 block">Upload Photos or Videos (Optional)</Label>
            <div className="border-2 border-dashed border-white/20 rounded-xl p-6 bg-white/5 hover:bg-white/10 transition-colors">
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                <Upload className="h-12 w-12 text-sage-400 mb-3" />
                <p className="text-white font-medium mb-1">Click to upload files</p>
                <p className="text-sm text-gray-400">Images or videos of your current bathroom/space</p>
              </label>
            </div>

            {/* Uploaded Files */}
            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((file, index) => (
                  <div key={`file-${file.name}-${file.size}-${index}`} className="flex items-center justify-between bg-white/10 p-3 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {file.type.startsWith('image/') ? (
                        <FileImage className="h-5 w-5 text-sage-400" />
                      ) : (
                        <FileVideo className="h-5 w-5 text-sage-400" />
                      )}
                      <span className="text-white text-sm">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button 
            type="submit" 
            size="lg" 
            className="w-full bg-sage-600 hover:bg-sage-700 text-white text-lg py-6 shadow-2xl"
          >
            Submit Quote Request
          </Button>

          <p className="text-sm text-gray-400 text-center">
            By submitting this form, you agree to be contacted by Magic C Plumbing regarding your request.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};
